/**
 * Which screen a URL asks for — and, since the secondary navigation, which tab
 * of it.
 *
 * Path-based rather than hash-based, because the console's CloudFront
 * distribution already rewrites a 404 to `/index.html` with a 200 — so a deep
 * link works, and a support engineer can paste one into a ticket.
 *
 * A parser rather than a router library: there are six screens, and the whole
 * of the routing logic being a pure function means it can be tested without a
 * DOM. Add a seventh screen here and the exhaustive switch in `App` stops
 * compiling until it is rendered.
 *
 * Nothing here decides what a viewer may *see*. The API answers 404 for a tenant
 * out of scope exactly as for one that never existed, so a hand-typed URL for
 * another customer's deployment looks the same as a typo. That is the whole of
 * the scope guarantee; a check here would be a convenience, not a control.
 */

/**
 * The tabs a screen has, each its own path (secondary navigation).
 *
 * **A tab is a route, not local state.** It deep-links, it can be pasted into a
 * ticket, the back button walks through it, and — because `App` renders a tab
 * through an exhaustive switch over these unions — adding one here stops the
 * console compiling until it is rendered. The first entry of each list is the
 * screen's default and has no path segment of its own, so every link built
 * before tabs existed still lands where it did.
 */
export const FLEET_TABS = ['instances', 'cost'] as const
export type FleetTab = (typeof FLEET_TABS)[number]

export const CUSTOMER_TABS = ['overview', 'approvals', 'users', 'cost', 'audit'] as const
export type CustomerTab = (typeof CUSTOMER_TABS)[number]

export const ACCOUNT_TABS = ['onboarding', 'instances', 'cost', 'settings'] as const
export type AccountTab = (typeof ACCOUNT_TABS)[number]

const isOneOf = <T extends string>(list: readonly T[], value: string | undefined): value is T =>
  value !== undefined && (list as readonly string[]).includes(value)

/** A tab's own path segment, or nothing for the screen's default tab. */
const tabSuffix = <T extends string>(list: readonly T[], tab: T | undefined): string =>
  tab === undefined || tab === list[0] ? '' : `/${tab}`

export type View =
  /**
   * Every instance, across every customer — the front door since IM-104.
   *
   * It is the screen at `/` because it answers what anyone actually arrives
   * with: something is running, something failed, or someone is waiting on a
   * deploy. The customer list is still here, one click away, because onboarding
   * and registration are per-customer work; it simply is not what the tool is
   * opened for.
   */
  | {
      readonly screen: 'fleet'
      /**
       * Absent is the default tab. Since the secondary navigation the Fleet is
       * Qrvey's own instances only — see `fleet.tsx` — and its Cost tab is
       * `/fleet/cost`.
       */
      readonly tab?: FleetTab
    }
  | { readonly screen: 'customers' }
  | { readonly screen: 'customer'; readonly tenantId: string; readonly tab?: CustomerTab }
  | {
      readonly screen: 'deployment'
      readonly tenantId: string
      readonly deploymentId: string
    }
  /** One cloud account, and whether it is ready to deploy into. */
  | {
      readonly screen: 'account'
      readonly tenantId: string
      readonly accountKey: string
      readonly tab?: AccountTab
    }
  /**
   * One run, at its own address (IM-105).
   *
   * Under the tenant because a run is, and because the read is tenant-scoped —
   * but it is a destination in its own right: the point of giving a run a path
   * is that the path can be pasted into a ticket while the deploy is still
   * going wrong.
   */
  | {
      readonly screen: 'run'
      readonly tenantId: string
      readonly runId: string
    }
  /** The audit trail. Not under a tenant: it spans them, and filters to one. */
  | { readonly screen: 'audit' }
  /** Who may reach which customers. Not under a tenant: it spans them. */
  | { readonly screen: 'access' }

/** Path for a view, so links are built in one place rather than interpolated. */
export const pathFor = (view: View): string => {
  switch (view.screen) {
    case 'fleet':
      // `/` stays the front door; a tab other than the default is under
      // `/fleet`, which `viewFor` also reads as the default.
      return view.tab === undefined || view.tab === FLEET_TABS[0] ? '/' : `/fleet/${view.tab}`
    case 'customers':
      return '/customers'
    case 'customer':
      return `/tenants/${encodeURIComponent(view.tenantId)}${tabSuffix(CUSTOMER_TABS, view.tab)}`
    case 'deployment':
      return `/tenants/${encodeURIComponent(view.tenantId)}/deployments/${encodeURIComponent(
        view.deploymentId,
      )}`
    case 'run':
      return `/tenants/${encodeURIComponent(view.tenantId)}/runs/${encodeURIComponent(view.runId)}`
    case 'account':
      return `/tenants/${encodeURIComponent(view.tenantId)}/accounts/${encodeURIComponent(
        view.accountKey,
      )}${tabSuffix(ACCOUNT_TABS, view.tab)}`
    case 'audit':
      return '/audit'
    case 'access':
      return '/access'
  }
}

/**
 * Reads a view out of a path.
 *
 * Anything unrecognised is the customer list rather than an error page: the
 * paths this app produces are the ones above, so an unknown one is a stale
 * bookmark or a typo, and showing someone where they can go beats telling them
 * where they cannot.
 */
export const viewFor = (pathname: string): View => {
  const segments = pathname.split('/').filter((segment) => segment !== '')

  if (segments[0] === 'audit') return { screen: 'audit' }
  if (segments[0] === 'access') return { screen: 'access' }
  if (segments[0] === 'customers') return { screen: 'customers' }
  if (segments[0] === 'fleet') {
    // `/fleet` is the default tab, like `/`; an unknown tab is too, for the
    // same stale-bookmark reason as everything else here.
    return isOneOf(FLEET_TABS, segments[1]) && segments[1] !== FLEET_TABS[0]
      ? { screen: 'fleet', tab: segments[1] }
      : { screen: 'fleet' }
  }
  // Anything unrecognised lands on the fleet rather than an error page — the
  // same reasoning as before, now that the fleet is what `/` means.
  if (segments[0] !== 'tenants' || segments[1] === undefined) return { screen: 'fleet' }
  const tenantId = decodeURIComponent(segments[1])

  if (segments[2] === 'runs' && segments[3] !== undefined) {
    return { screen: 'run', tenantId, runId: decodeURIComponent(segments[3]) }
  }
  if (segments[2] === 'deployments' && segments[3] !== undefined) {
    return { screen: 'deployment', tenantId, deploymentId: decodeURIComponent(segments[3]) }
  }
  // An account key carries a colon (`aws:123456789012`), so it is encoded in the
  // path and decoded here rather than being split on.
  if (segments[2] === 'accounts' && segments[3] !== undefined) {
    const accountKey = decodeURIComponent(segments[3])
    return isOneOf(ACCOUNT_TABS, segments[4]) && segments[4] !== ACCOUNT_TABS[0]
      ? { screen: 'account', tenantId, accountKey, tab: segments[4] }
      : { screen: 'account', tenantId, accountKey }
  }
  // Only a segment with nothing after it is a tab: `/tenants/acme/cost/x` is not
  // a place this console builds, so it reads as the customer like any typo.
  if (
    segments.length === 3 &&
    isOneOf(CUSTOMER_TABS, segments[2]) &&
    segments[2] !== CUSTOMER_TABS[0]
  ) {
    return { screen: 'customer', tenantId, tab: segments[2] }
  }
  return { screen: 'customer', tenantId }
}
